import { ReactNode } from 'react';
import {
  Button,
  Col,
  Flex,
  FormContainer,
  Icon,
  IconButton,
  Modal,
  Table,
  Text,
  Title,
} from '@wyz/ui';
import { DEFAULT_EURO_SIGN, useModalState } from '@wyz/utils';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { PrivateComponent, TextField } from '~/common/components';
import { Trans, useI18n, useLocale } from '~/common/context';
import { useManageQuoteServicePricingPolicies } from '~/purchase/api';
import { QuoteHelpers } from '~/purchase/helpers';
import {
  PRICE_TYPE_ENUM,
  SERVICE_TYPE,
  servicePriceEditFormSchema,
} from '~/purchase/schemas';
import {
  IActiveQuoteService,
  IQuoteCartService,
  IQuoteCartServicePriceEditForm,
} from '~/purchase/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'lodash-es';
import { ROLES } from '~/common/helpers';
import { QuotePriceEditInput } from '~/purchase/components';
import { useQuoteCartContext } from '~/purchase/context';
/* eslint-disable-next-line @wyz/module/no-relative-import */
import './services-form.scss';
import { CenteredSpinner } from '@wyz/ui';
type AddedServiceRowProps = {
  service: IActiveQuoteService;
};

const FALLBACK_CURRENCY = 'EUR';

function AddedServiceRow({ service }: AddedServiceRowProps) {
  const locale = useLocale();
  const quoteCartContext = useQuoteCartContext();
  const cartService = quoteCartContext.data.services.find(
    (cartService) =>
      cartService.serviceCode === service.serviceCode &&
      cartService.vehicleType?.name === service.tyreType?.name,
  );

  const currencyCode = quoteCartContext.data.currency ?? FALLBACK_CURRENCY;
  const currencySymbol = locale.formatters.currency(currencyCode);

  const onDelete = () => {
    if (cartService) {
      quoteCartContext.actions.removeService.process(cartService.id);
    }
  };

  const [modal, modalHandlers] = useModalState<'DISCOUNT_MODAL'>();

  const price = `${currencySymbol} ${cartService?.priceTaxIncl.toFixed(2)}`;

  return (
    <tr>
      <td className='bg-white px-3 py-2 text-center'>
        <Text
          color='tertiary-bold'
          weight='bold'
          id={`added-service-code-${service.serviceCode}`}
        >
          {service.serviceCode || '-'}
        </Text>
      </td>
      <td className='bg-white px-3 py-2'>
        <Text
          heading='normal'
          color='secondary-gray'
          id={`added-service-description-${service.serviceCode}`}
        >
          {service.description || '-'}
        </Text>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <Text
          heading='normal'
          id={`added-service-vehicle-type-${service.serviceCode}`}
        >
          <span className='badge bg-info-bg text-info-text fw-semi-bold'>
            {service.tyreType?.name || '-'}
          </span>
        </Text>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <div className='d-flex align-items-center justify-content-center gap-1'>
          <Text
            color={cartService?.manualDiscount ? 'warning' : 'secondary'}
            weight='bold'
            id={`added-service-price-${service.serviceCode}`}
            className='no-wrap'
          >
            {cartService?.manualDiscount &&
            cartService.discountPercentage &&
            cartService.basePriceTaxIncl > 0 ? (
              <Trans
                i18nKey='app.purchase.quote_cart_page.price_edit.price_discount'
                values={{
                  price,
                  discountPercentage: -cartService.discountPercentage,
                }}
              />
            ) : (
              price
            )}
          </Text>
          <PrivateComponent roles={[ROLES.ROLE_QUOTE_CART_UPDATE_PRICE]}>
            <IconButton
              id={`edit-price-${service.serviceCode}-${service.tyreType?.name || ''}`}
              variant='link'
              icon='pencil-line'
              className='ms-2 cursor-pointer'
              onClick={() => modalHandlers.open('DISCOUNT_MODAL')}
            />
          </PrivateComponent>
        </div>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <Button
          id={`delete-service-${service.serviceCode}-${service.tyreType?.name || ''}`}
          variant='secondary'
          onClick={onDelete}
          isLoading={quoteCartContext.actions.removeService.isLoading}
        >
          <Trans i18nKey='app.common.buttons.delete' />
          <Icon icon='trash' className='ms-2' />
        </Button>
      </td>
      {modal === 'DISCOUNT_MODAL' && !isNil(cartService) && (
        <EditPriceModal
          onClose={modalHandlers.close}
          service={cartService}
          currency={currencyCode}
          vatPercentage={quoteCartContext.data.vatPercentage}
          onSubmit={(price) => {
            quoteCartContext.actions.updatePrice.process(
              {
                services: [{ id: cartService.id, price }],
              },
              modalHandlers.close,
            );
          }}
        />
      )}
    </tr>
  );
}

type EditPriceModalProps = {
  onSubmit: (price: number) => void;
  onClose: () => void;
  service: IQuoteCartService;
  currency: string;
  vatPercentage: number;
};

function EditPriceModal({
  onSubmit,
  onClose,
  service,
  currency,
  vatPercentage,
}: EditPriceModalProps) {
  const basePriceTaxExcl = QuoteHelpers.getPriceTaxExcl(
    service.basePriceTaxIncl,
    vatPercentage,
  );

  const item = {
    unitPriceTaxIncl: service.priceTaxIncl,
    currency,
    quantity: 1,
    discountBasePrice: basePriceTaxExcl,
    marginBasePrice: basePriceTaxExcl,
  };

  const methods = useForm<IQuoteCartServicePriceEditForm>({
    resolver: zodResolver(servicePriceEditFormSchema),
    defaultValues: {
      item: QuoteHelpers.getQuotePriceFieldValues(
        item.unitPriceTaxIncl,
        item.discountBasePrice,
        item.marginBasePrice,
        vatPercentage,
        item.quantity,
        'DISCOUNT',
      ),
    },
  });

  return (
    <Modal.Container onClose={onClose} open={true} size='lg' centered>
      <Modal.Header closeButton>
        <Title heading='lg' weight='bold' color='secondary'>
          <Trans i18nKey='app.purchase.quote_cart_page.price_edit.title' />
        </Title>
      </Modal.Header>
      <Modal.Body>
        <FormContainer
          methods={methods}
          submitHandler={(data) => onSubmit(data.item.discountedPriceTaxIncl)}
        >
          <QuotePriceEditInput
            baseName='item'
            item={item}
            vatPercentage={vatPercentage}
            adjustmentType='DISCOUNT'
            itemType={'SERVICE'}
          />
        </FormContainer>
        <IconButton
          className='my-4'
          id='delete-discount'
          variant='tertiary-grey'
          icon='trash'
          onClick={() =>
            methods.setValue(
              'item',
              QuoteHelpers.getQuotePriceFieldValues(
                service.basePriceTaxIncl,
                item.discountBasePrice,
                item.marginBasePrice,
                vatPercentage,
                item.quantity,
                'DISCOUNT',
              ),
            )
          }
        >
          <Trans i18nKey='app.purchase.quote_cart_page.price_edit.remove_discount' />
        </IconButton>
        <Flex className='gap-3 mt-4'>
          <Button id='cancel' variant='secondary' onClick={onClose} fullWidth>
            <Trans i18nKey='app.common.button.cancel' />
          </Button>
          <Button
            form='edit-price-form'
            id='submit'
            variant='primary'
            fullWidth
            onClick={methods.handleSubmit((data) => {
              onSubmit(data.item.discountedPriceTaxIncl);
              onClose();
            })}
          >
            <Trans i18nKey='app.common.button.apply' />
          </Button>
        </Flex>
      </Modal.Body>
    </Modal.Container>
  );
}

type AvailableServiceRowProps = {
  service: IActiveQuoteService;
};

function AvailableServiceRow({ service }: AvailableServiceRowProps) {
  const locale = useLocale();
  const quoteCartContext = useQuoteCartContext();
  const servicePrice =
    service.priceType === 'TAX_EXCLUDED'
      ? QuoteHelpers.getPriceTaxIncl(
          service.priceValue.c,
          quoteCartContext.data.vatPercentage,
        )
      : service.priceValue.c;
  const currencyCode = quoteCartContext.data.currency;

  const currencySymbol = currencyCode
    ? locale.formatters.currency(currencyCode)
    : DEFAULT_EURO_SIGN;

  const onAdd = () => {
    quoteCartContext.actions.addService.process({
      serviceName: service.description,
      serviceType: service.serviceType,
      serviceCode: service.serviceCode,
      basePriceTaxIncl: servicePrice,
      priceTaxIncl: servicePrice,
      vehicleType: service.tyreType,
    });
  };

  return (
    <tr>
      <td className='bg-white px-3 py-2 text-center'>
        <Text
          color='tertiary-bold'
          weight='bold'
          id={`available-service-code-${service.serviceCode}`}
        >
          {service.serviceCode || '-'}
        </Text>
      </td>
      <td className='bg-white px-3 py-2'>
        <Text
          heading='normal'
          color='secondary-gray'
          id={`available-service-description-${service.serviceCode}`}
        >
          {service.description || '-'}
        </Text>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <Text
          heading='normal'
          id={`available-service-vehicle-type-${service.serviceCode}`}
        >
          <span className='badge bg-info-bg text-info-text fw-semi-bold'>
            {service.tyreType?.name || '-'}
          </span>
        </Text>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <Text
          color='secondary'
          weight='bold'
          id={`available-service-price-${service.serviceCode}`}
        >
          {currencySymbol}
          {servicePrice.toFixed(2)}
        </Text>
      </td>
      <td className='bg-white px-3 py-2 text-center'>
        <Button
          id={`add-service-${service.serviceCode}-${service.tyreType?.name || ''}`}
          variant='primary'
          onClick={onAdd}
          isLoading={quoteCartContext.actions.addService.isLoading}
        >
          <Trans i18nKey='app.purchase.quote_search_page.add_to_quote' />
          <Icon icon='shopping-cart' className='ms-2' />
        </Button>
      </td>
    </tr>
  );
}

const MIN_CHARACTER = 3;

type ServicesFormProps = {
  children?: ReactNode;
};

function ServicesForm({ children }: ServicesFormProps) {
  const quoteCartContext = useQuoteCartContext();
  const { t } = useI18n();
  const methods = useFormContext();

  const watchedSearchQuery = useWatch({
    name: 'searchQuery',
    control: methods.control,
  });

  const manageServicePricingPolicies = useManageQuoteServicePricingPolicies({
    list: {
      enabled: true,
      args: {
        serviceTypes: [SERVICE_TYPE.CUSTOM, SERVICE_TYPE.GEOMETRY],
        tyresMetadata: quoteCartContext.data.vehicleTypes.map(
          (vehicleType) => ({
            vehicleTypePublicKey: vehicleType,
          }),
        ),
        query:
          watchedSearchQuery?.length >= MIN_CHARACTER
            ? watchedSearchQuery
            : undefined,
      },
    },
  });

  const addedServices = quoteCartContext.data.services
    .map((cartService) => {
      const service = manageServicePricingPolicies.list.data?.data.find(
        (s) =>
          s.serviceCode === cartService.serviceCode &&
          s.tyreType?.name === cartService.vehicleType?.name,
      );
      return service
        ? {
            ...service,
            priceTaxIncl: cartService.priceTaxIncl,
          }
        : null;
    })
    .filter(Boolean) as IActiveQuoteService[];

  const availableServices =
    manageServicePricingPolicies.list.data?.data.filter(
      (service) =>
        !quoteCartContext.data.services.some(
          (cartService) =>
            cartService.serviceCode === service.serviceCode &&
            cartService.vehicleType?.name === service.tyreType?.name,
        ),
    ) || [];

  return (
    <>
      <div className='mb-4'>
        <Title heading='lg' weight='bold' color='secondary'>
          <Trans i18nKey='app.common.quote.services' />
        </Title>
      </div>

      {addedServices.length > 0 ? (
        <div className='mb-5'>
          <div className='services-table-wrapper'>
            <Table>
              <thead>
                <tr>
                  <th className='text-center' style={{ width: '15%' }}>
                    <Trans i18nKey='app.common.service.quote.code' />
                  </th>
                  <th className='text-center' style={{ width: '35%' }}>
                    <Trans i18nKey='app.common.description' />
                  </th>
                  <th className='text-center' style={{ width: '15%' }}>
                    <Trans i18nKey={'app.common.vehicle_category'} />
                  </th>
                  <th className='text-center' style={{ width: '15%' }}>
                    <Trans i18nKey='app.purchase.quote_cart_page.services.table.price' />
                  </th>
                  <th className='text-center' style={{ width: '20%' }}>
                    <Trans i18nKey='app.common.actions' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {addedServices.map((service) => (
                  <AddedServiceRow
                    key={`${service.serviceCode}-${service.tyreType?.name || ''}`}
                    service={service}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      ) : (
        <div className='mb-5 text-center py-4 bg-neutral-grey-1 rounded'>
          <Text color='tertiary'>
            <Trans i18nKey='app.purchase.quote_cart_page.services.no_services' />
          </Text>
        </div>
      )}

      <div className='mt-5'>
        <div className='mb-3'>
          <Title heading='lg' weight='bold' color='secondary'>
            <Trans i18nKey={'app.purchase.quote_cart_page.services.add_new'} />
          </Title>
        </div>

        <div className='grid mb-3'>
          <Col bsPrefix='g-col' md={6}>
            <TextField
              label={
                <Trans i18nKey='app.purchase.quote_cart_page.services.search.label' />
              }
              placeholder={t(
                'app.purchase.quote_cart_page.services.search.placeholder',
              )}
              type='search'
              name='searchQuery'
            />
          </Col>
        </div>

        <div className='services-table-wrapper'>
          <Table>
            <thead>
              <tr>
                <th className='text-center' style={{ width: '15%' }}>
                  <Trans i18nKey='app.common.service.quote.code' />
                </th>
                <th className='text-center' style={{ width: '30%' }}>
                  <Trans i18nKey='app.common.description' />
                </th>
                <th className='text-center' style={{ width: '15%' }}>
                  <Trans i18nKey={'app.common.vehicle_category'} />
                </th>
                <th className='text-center' style={{ width: '15%' }}>
                  <Trans i18nKey='app.purchase.quote_cart_page.services.table.price' />
                </th>
                <th className='text-center' style={{ width: '35%' }}>
                  <Trans i18nKey='app.common.actions' />
                </th>
              </tr>
            </thead>
            <tbody>
              {availableServices.map((service) => (
                <AvailableServiceRow
                  key={`${service.serviceCode}-${service.tyreType?.name || ''}`}
                  service={service}
                />
              ))}
              {children}
            </tbody>
          </Table>
        </div>
      </div>
    </>
  );
}

export default ServicesForm;

type GeometryServiceRowProps = {
  manageServicePricingPolicies: Pick<
    ReturnType<typeof useManageQuoteServicePricingPolicies>,
    'list'
  >;
};

export function GeometryServiceRow({
  manageServicePricingPolicies,
}: GeometryServiceRowProps) {
  const hasGeometryService =
    manageServicePricingPolicies.list.data?.data.some(
      (service: IActiveQuoteService) =>
        service.serviceType === SERVICE_TYPE.GEOMETRY,
    ) ?? false;

  if (manageServicePricingPolicies.list.isLoading) return <CenteredSpinner />;

  if (hasGeometryService) return null;

  return (
    <AvailableServiceRow
      service={{
        priceValue: {
          c: 0,
          cg1: 0,
          cg2: 0,
          cg3: 0,
          cg4: 0,
          cg5: 0,
        },
        serviceCode: '-',
        description: '-',
        serviceType: SERVICE_TYPE.GEOMETRY,
        priceType:
          manageServicePricingPolicies.list.data?.data.at(0)?.priceType ??
          PRICE_TYPE_ENUM.TAX_INCLUDED,
      }}
    />
  );
}
